import { on } from '@ngrx/store';

import { LoadingState } from 'src/app/utilities/state.util';

import { BlueSageQueueActions } from './bluesage-queue.actions';
import { BlueSageQueueState } from './bluesage-queue.state';

/**
 * A resolved payment is removed from the page rather than left in place with a
 * new status: the default view is "what still needs a human", and a row that
 * stays put after being dealt with invites a second operator to deal with it
 * again. The count comes from the summary, which the effect reloads alongside.
 */
const removeResolved = (
    state: BlueSageQueueState,
    { payment }: { payment: { id: string } },
): BlueSageQueueState => ({
    ...state,
    resolvingId: undefined,
    selectedId: undefined,
    jobCode: '',
    jobMatch: undefined,
    lookingUp: false,
    dismissReason: '',
    page: state.page
        ? {
            ...state.page,
            total: Math.max(0, state.page.total - 1),
            payments: state.page.payments.filter((p) => p.id !== payment.id),
        }
        : state.page,
});

export const blueSageQueueReducers = [

    on(
        BlueSageQueueActions.pageOpened,
        BlueSageQueueActions.refreshButtonClicked,
        (state: BlueSageQueueState): BlueSageQueueState => ({
            ...state,
            loading: LoadingState.LOADING,
            loadFailed: false,
        }),
    ),

    on(
        BlueSageQueueActions.statusFilterChanged,
        (state: BlueSageQueueState, { status }): BlueSageQueueState => ({
            ...state,
            statusFilter: status,
            // A filter change that kept the offset would land the operator on a
            // page that no longer exists.
            skip: 0,
            loading: LoadingState.LOADING,
            // Requirement: K8 - `loadFailed` describes the MOST RECENT attempt
            // only. The template checks it before `loading`, so leaving it set
            // from an earlier failure shows "Could not load the review queue"
            // over a request that is working fine.
            loadFailed: false,
        }),
    ),

    on(
        BlueSageQueueActions.pageChanged,
        (state: BlueSageQueueState, { skip, limit }): BlueSageQueueState => ({
            ...state,
            skip,
            limit,
            loading: LoadingState.LOADING,
            // Requirement: K8 - see `statusFilterChanged`: a new load clears the
            // previous attempt's failure.
            loadFailed: false,
        }),
    ),

    on(
        BlueSageQueueActions.loadPaymentsSuccess,
        (state: BlueSageQueueState, { page }): BlueSageQueueState => ({
            ...state,
            page,
            loading: LoadingState.LOADED,
            loadFailed: false,
        }),
    ),

    on(
        BlueSageQueueActions.loadPaymentsError,
        (state: BlueSageQueueState): BlueSageQueueState => ({
            ...state,
            // There is no ERROR member on LoadingState, so the failure is carried
            // separately. Settling on LOADED alone made a failed load render as
            // "nothing to review", which on THIS screen is a lie about money.
            loading: LoadingState.LOADED,
            loadFailed: true,
            // Drop the stale page too — showing rows fetched before the failure
            // alongside an error is worse than showing neither.
            page: undefined,
        }),
    ),

    on(
        BlueSageQueueActions.loadSummarySuccess,
        (state: BlueSageQueueState, { summary }): BlueSageQueueState => ({
            ...state,
            summary,
        }),
    ),

    on(
        BlueSageQueueActions.paymentSelected,
        (state: BlueSageQueueState, { paymentId }): BlueSageQueueState => ({
            ...state,
            selectedId: paymentId,
            // Pre-fill with the code the crew actually keyed on the terminal. It
            // is already on the row, and in the common case — a job the
            // middleware had never transacted, so could not match — it is exactly
            // right and the operator only has to confirm the job it names.
            jobCode: paymentId
                ? state.page?.payments.find((p) => p.id === paymentId)?.jobCode ?? ''
                : '',
            jobMatch: undefined,
            // The pre-filled code is looked up the moment the row opens
            // (`lookupBlueSageJob` listens to this action), so the panel says so
            // rather than sitting blank until the answer lands — reported on
            // 2026-09-03 as "I have to type in the field for it to search".
            lookingUp: Boolean(
                paymentId && state.page?.payments.find((p) => p.id === paymentId)?.jobCode?.trim(),
            ),
            lookupFailed: false,
            dismissReason: '',
        }),
    ),

    on(
        BlueSageQueueActions.jobCodeChanged,
        (state: BlueSageQueueState, { jobCode }): BlueSageQueueState => ({
            ...state,
            jobCode,
            // Drop the previous match the moment the text changes. Leaving it on
            // screen while it no longer describes what is typed is how somebody
            // attaches money to the job they looked at a second ago.
            jobMatch: undefined,
            lookingUp: Boolean(jobCode.trim()),
            lookupFailed: false,
        }),
    ),

    on(
        BlueSageQueueActions.jobLookupSuccess,
        (state: BlueSageQueueState, { match, lookupKey }): BlueSageQueueState => {
            // Requirement: K25 - discard a success from a superseded lookup. The
            // debounce lives outside switchMap (of() completes synchronously, so
            // debounceTime inside switchMap would flush immediately), which means
            // a new action during the 300 ms window does NOT cancel an in-flight
            // request. This guard prevents a stale result from writing a jobMatch
            // for a payment the operator is no longer looking at.
            const currentPaymentId = state.selectedId ?? null;
            if (lookupKey.code !== state.jobCode.trim()
                || lookupKey.paymentId !== currentPaymentId) {
                return state;
            }
            return {
                ...state,
                jobMatch: match,
                lookingUp: false,
                lookupFailed: false,
            };
        },
    ),

    on(
        BlueSageQueueActions.jobLookupError,
        (state: BlueSageQueueState): BlueSageQueueState => ({
            ...state,
            jobMatch: undefined,
            lookingUp: false,
            // Said out loud rather than left as an empty panel: "the search is
            // broken" and "no job has that code" are different answers and only
            // one of them means the operator should keep typing.
            lookupFailed: true,
        }),
    ),

    on(
        BlueSageQueueActions.dismissReasonChanged,
        (state: BlueSageQueueState, { dismissReason }): BlueSageQueueState =>
            ({ ...state, dismissReason }),
    ),

    on(
        BlueSageQueueActions.completeHoldButtonClicked,
        (state: BlueSageQueueState, { paymentId, transactionId }): BlueSageQueueState => ({
            ...state,
            resolvingId: paymentId,
            resolvingHoldId: transactionId,
        }),
    ),

    on(
        BlueSageQueueActions.completeHoldSuccess,
        (state: BlueSageQueueState, { payment }): BlueSageQueueState => ({
            ...state,
            resolvingId: undefined,
            resolvingHoldId: undefined,
            selectedId: undefined,
            page: state.page
                ? {
                    ...state.page,
                    payments: state.page.payments.map((p) => (p.id === payment.id ? payment : p)),
                }
                : state.page,
        }),
    ),

    on(
        BlueSageQueueActions.completeHoldError,
        (state: BlueSageQueueState): BlueSageQueueState => ({
            ...state,
            resolvingId: undefined,
            resolvingHoldId: undefined,
        }),
    ),

    on(
        BlueSageQueueActions.attachButtonClicked,
        BlueSageQueueActions.dismissButtonClicked,
        (state: BlueSageQueueState, { paymentId }): BlueSageQueueState => ({
            ...state,
            resolvingId: paymentId,
        }),
    ),

    on(
        BlueSageQueueActions.attachSuccess,
        BlueSageQueueActions.dismissSuccess,
        removeResolved,
    ),

    on(
        BlueSageQueueActions.attachError,
        BlueSageQueueActions.dismissError,
        (state: BlueSageQueueState): BlueSageQueueState => ({
            ...state,
            // The row stays selected and the form keeps what was typed: the
            // failure is usually "that job has no invoice", and retyping the job
            // code to try again as a deposit is pure friction.
            resolvingId: undefined,
        }),
    ),
];
